/**
 * schemaTypes.js — single source of truth for classifying a backend schema
 * into a "schema_type".
 *
 * Background: the app stores data in several backend schemas ("tracking",
 * "single", ...). The `schema` string identifies the physical namespace
 * (where the Aurora table / Parquet directory lives) and must be sent to the
 * backend verbatim so it can locate the data.
 *
 * Separately, some schemas hold MULTIPLE WAVES of the same questions (a time
 * series) while others hold a single snapshot. The frontend and backend need
 * to branch on that distinction (show the wave selector, add `wave` to the
 * GROUP BY, read the wave-partitioned Parquet layout, etc.). Historically that
 * branch was hard-coded as `schema === "tracking"`, which conflated "lives in
 * the tracking namespace" with "has waves". We now classify schemas into a
 * `schema_type` — "tracking" (multi-wave) or "single" — and branch on that.
 *
 * To onboard a secondary tracking schema in future, add it to TRACKING_SCHEMAS
 * here and to TRACKING_SCHEMAS in core/views.py. Everything else (frontend
 * gating + the `schema_type` posted to the backend) follows automatically.
 */

/** Schemas that hold multiple waves and should behave like "tracking". */
export const TRACKING_SCHEMAS = new Set(["tracking","energy","mccain"]);

export const SCHEMA_TYPE_TRACKING = "tracking";
export const SCHEMA_TYPE_SINGLE = "single";

/**
 * Classify a raw schema string into its schema_type.
 * @param {string|undefined|null} schema  e.g. "tracking", "single", "public"
 * @returns {"tracking"|"single"}
 */
export function schemaTypeFor(schema) {
  return TRACKING_SCHEMAS.has(schema) ? SCHEMA_TYPE_TRACKING : SCHEMA_TYPE_SINGLE;
}

/** True when the schema holds multiple waves (was: schema === "tracking"). */
export function isTrackingSchema(schema) {
  return schemaTypeFor(schema) === SCHEMA_TYPE_TRACKING;
}

/**
 * Convenience for the common `group.schema === "tracking"` group-level check.
 * @param {{schema?: string}|undefined|null} group
 */
export function isMultiWaveGroup(group) {
  return isTrackingSchema(group?.schema);
}

/**
 * The schema the backend assumes when a request omits one (see
 * level_data_arrow in core/views.py). Legacy default, kept for compatibility.
 */
export const DEFAULT_SCHEMA = "tracking";

/**
 * Build the `{ schema, schema_type }` pair for a data request body.
 *
 * Always use this rather than setting the two fields by hand: the posted
 * schema_type now OVERRIDES the backend's own derivation, so the two must
 * agree. In particular an absent/blank schema must resolve to DEFAULT_SCHEMA
 * on both sides — posting a bare `schemaTypeFor(undefined)` would say "single"
 * while the backend reads the same request as "tracking".
 *
 * @param {string|undefined|null} schema
 * @returns {{schema: string, schema_type: "tracking"|"single"}}
 */
export function schemaRequestFields(schema) {
  const resolved =
    (typeof schema === "string" && schema.trim()) || DEFAULT_SCHEMA;
  return { schema: resolved, schema_type: schemaTypeFor(resolved) };
}
